import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const formatErr = (detail) => {
  if (!detail) return "Login failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = location.state?.from?.pathname || "/admin/orders";
      navigate(dest, { replace: true });
    }
  }, [user, location, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Logged in");
      const dest = location.state?.from?.pathname || "/admin/orders";
      navigate(dest, { replace: true });
    } catch (e2) {
      toast.error(formatErr(e2?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[80vh] grid place-items-center px-4 py-16">
      <div className="w-full max-w-md border border-[#E2E8F0] bg-white p-8">
        <div className="flex items-center gap-2 sb-mono-label">
          <Lock className="w-3.5 h-3.5" /> Admin · Internal access
        </div>
        <h1
          className="mt-3 text-3xl font-semibold text-[#1B3A5C] tracking-tight"
          style={{ fontFamily: "Outfit" }}
        >
          Sign in
        </h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Authorised personnel only. Sterling Bio operations team.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <label className="block">
            <span className="sb-mono-label">Email</span>
            <div className="mt-2 flex items-center border border-[#E2E8F0] focus-within:border-[#2C8C9C]">
              <Mail className="w-4 h-4 text-[#64748B] ml-3" />
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sterlingbio.example"
                autoComplete="username"
                className="flex-1 px-3 py-3 text-sm outline-none bg-white"
                required
              />
            </div>
          </label>

          <label className="block">
            <span className="sb-mono-label">Password</span>
            <div className="mt-2 flex items-center border border-[#E2E8F0] focus-within:border-[#2C8C9C]">
              <KeyRound className="w-4 h-4 text-[#64748B] ml-3" />
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="flex-1 px-3 py-3 text-sm outline-none bg-white"
                required
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full bg-[#1B3A5C] text-white px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-[#254D7A] transition-colors disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-xs font-mono uppercase tracking-wider text-[#64748B]">
          Sessions expire after 12 hours.
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
